import { logDebug, logError } from '@ptek/client-logger';
import { useCallback, useEffect, useRef } from 'react';
import * as audioRecorder from '../../../api-clients/audioRecorder';
import { FEATURE_FLAGS } from '../../../types/featureFlags';
import { hasFeature } from '../../../utils/feature-util';
import { TranscriptLine } from '../../../types/recorder';
import {
  STOP_RECORDING_STATE,
  StopRecordingState,
  useStopRecordingStore,
} from '../stores/stopRecordingStore';

/**
 * Transcript waiting timeout in milliseconds (30 seconds)
 * This is longer than the internal 5-second timeout in waitForTranscriptCompletion
 * to allow for network delays and processing time
 */
const TRANSCRIPT_TIMEOUT_MS = 30000;

/**
 * Default error message for transcript timeout
 */
const TRANSCRIPT_TIMEOUT_ERROR_MESSAGE =
  'Transcript processing timed out. The recording may have been stopped, but the final transcript is still being processed.';

/**
 * Return type for useStopRecordingStateMachine hook
 */
export interface UseStopRecordingStateMachineResult {
  state: StopRecordingState;
  errorMessage?: string;
  transcriptData?: { transcriptText: string };
  executeStopRecording: (options: {
    onRecordingStopped?: () => void;
    onRecordingError?: (error: Error) => void;
    handleTranscriptComplete: (
      transcriptLines: TranscriptLine[]
    ) => Promise<{ transcriptText: string }>;
  }) => Promise<{ transcriptText: string }>;
  reset: () => void;
}

/**
 * Hook for managing stop recording state machine
 *
 * Behavior is controlled by FEATURE_FLAGS.CONNECTION_RETRY:
 * - Flag OFF: Returns null, no behavior changes
 * - Flag ON: Returns state machine with enhanced error handling and timeout
 *
 * Features when enabled:
 * - State machine: idle → stopping → waiting_transcript → success/error
 * - 30-second timeout: Auto-transitions to error if stuck in waiting_transcript
 * - Error differentiation: Separate error states for stopping vs transcript phases
 *
 * @returns Stop recording state and controls, or null if feature flag is disabled
 */
export const useStopRecordingStateMachine =
  (): UseStopRecordingStateMachineResult | null => {
    const isFeatureEnabled = hasFeature(FEATURE_FLAGS.CONNECTION_RETRY);

    const currentState = useStopRecordingStore((state) => state.state);
    const errorMessage = useStopRecordingStore((state) => state.errorMessage);
    const transcriptData = useStopRecordingStore(
      (state) => state.transcriptData
    );
    const setState = useStopRecordingStore((state) => state.setState);
    const reset = useStopRecordingStore((state) => state.reset);

    const timeoutRef = useRef<number | null>(null);

    /**
     * Clears any active timeout timer
     */
    const clearTimeoutTimer = useCallback(() => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }, []);

    /**
     * Starts the transcript waiting timeout timer
     * Transitions to error state after TRANSCRIPT_TIMEOUT_MS
     */
    const startTimeoutTimer = useCallback(() => {
      clearTimeoutTimer();

      logDebug(
        'useStopRecordingStateMachine',
        '⏰ Starting 30-second timeout timer'
      );

      timeoutRef.current = window.setTimeout(() => {
        logError(
          'useStopRecordingStateMachine',
          '⏰ TIMEOUT: Transcript wait exceeded 30 seconds',
          {
            message: TRANSCRIPT_TIMEOUT_ERROR_MESSAGE,
          }
        );
        setState(
          STOP_RECORDING_STATE.ERROR_TRANSCRIPT,
          TRANSCRIPT_TIMEOUT_ERROR_MESSAGE
        );
      }, TRANSCRIPT_TIMEOUT_MS);
    }, [clearTimeoutTimer, setState]);

    /**
     * Executes the stop recording flow with state machine management
     */
    const executeStopRecording = useCallback(
      async (options: {
        onRecordingStopped?: () => void;
        onRecordingError?: (error: Error) => void;
        handleTranscriptComplete: (
          transcriptLines: TranscriptLine[]
        ) => Promise<{ transcriptText: string }>;
      }): Promise<{ transcriptText: string }> => {
        const {
          onRecordingStopped,
          onRecordingError,
          handleTranscriptComplete,
        } = options;

        logDebug(
          'useStopRecordingStateMachine',
          '🎬 Starting stop recording flow'
        );

        let currentPhase: StopRecordingState = STOP_RECORDING_STATE.IDLE;

        setState(STOP_RECORDING_STATE.IDLE);

        try {
          currentPhase = STOP_RECORDING_STATE.STOPPING;
          setState(currentPhase);
          logDebug(
            'useStopRecordingStateMachine',
            '⏹️ State: STOPPING - Stopping audio recording'
          );

          await new Promise<void>((resolve, reject) => {
            audioRecorder.stopRecording({
              onRecordingStopped: () => {
                logDebug(
                  'useStopRecordingStateMachine',
                  '✅ Audio recording stopped successfully'
                );
                onRecordingStopped?.();
                resolve();
              },
              onRecordingError: (error) => {
                logError(
                  'useStopRecordingStateMachine',
                  '❌ Error stopping audio recording',
                  error
                );
                setState(
                  STOP_RECORDING_STATE.ERROR_STOPPING,
                  error.message || 'Failed to stop recording'
                );

                onRecordingError?.(error);
                reject(error);
              },
            });
          });

          currentPhase = STOP_RECORDING_STATE.WAITING_TRANSCRIPT;
          setState(currentPhase);
          startTimeoutTimer();
          logDebug(
            'useStopRecordingStateMachine',
            '⏳ State: WAITING_TRANSCRIPT - Waiting for final transcript (30s timeout)'
          );

          const transcriptLines =
            await audioRecorder.waitForTranscriptCompletion();

          logDebug('useStopRecordingStateMachine', '📄 Transcript received', {
            lineCount: transcriptLines.length,
            totalText:
              transcriptLines
                .map((l) => l.text)
                .join(' ')
                .substring(0, 100) + '...',
          });

          clearTimeoutTimer();

          logDebug('useStopRecordingStateMachine', '🔄 Processing transcript');
          const transcriptData =
            await handleTranscriptComplete(transcriptLines);

          logDebug('useStopRecordingStateMachine', '✅ Transcript processed', {
            transcriptLength: transcriptData?.transcriptText?.length || 0,
          });

          setState(
            STOP_RECORDING_STATE.SUCCESS,
            undefined,
            transcriptData || { transcriptText: '' }
          );

          logDebug(
            'useStopRecordingStateMachine',
            '🎉 State: SUCCESS - Stop recording completed successfully'
          );

          return transcriptData || { transcriptText: '' };
        } catch (error) {
          clearTimeoutTimer();

          const errorMessage =
            error instanceof Error ? error.message : String(error);

          const errorState =
            currentPhase === STOP_RECORDING_STATE.STOPPING
              ? STOP_RECORDING_STATE.ERROR_STOPPING
              : STOP_RECORDING_STATE.ERROR_TRANSCRIPT;

          const errorStateLabel =
            errorState === STOP_RECORDING_STATE.ERROR_STOPPING
              ? 'ERROR_STOPPING'
              : 'ERROR_TRANSCRIPT';

          logError(
            'useStopRecordingStateMachine',
            `❌ State: ${errorStateLabel}`,
            {
              phase: currentPhase,
              error: errorMessage,
            }
          );

          setState(errorState, errorMessage);

          const err = error instanceof Error ? error : new Error(errorMessage);
          onRecordingError?.(err);

          return { transcriptText: '' };
        }
      },
      [setState, startTimeoutTimer, clearTimeoutTimer]
    );

    /**
     * Cleanup timeout on unmount
     */
    useEffect(() => {
      return () => {
        clearTimeoutTimer();
      };
    }, [clearTimeoutTimer]);

    if (!isFeatureEnabled) {
      return null;
    }

    return {
      state: currentState,
      errorMessage,
      transcriptData,
      executeStopRecording,
      reset,
    };
  };
